import React, { memo, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { Button, Flex, Tooltip, Typography } from "antd"
import { oldRoutes } from "router/old-routes"
import PhotoSelector from "components/form/PhotoSelector"
import Input from "components/form/Input"
import RichTextEditor, { toEditorHtml } from "components/common/RichTextEditor"

import { useTranslation } from "react-i18next"
import { useConfirmModal } from "modules/modals/hooks/useConfirmModal"

import { useDispatch } from "react-redux"
import { removeTemplate } from "store/templates"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { Icon as UIIcon } from "ui/Icon"
import { colors } from "themes/variables"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import styles from "./TemplateForm.module.css"

const { Text } = Typography

TemplateForm.propTypes = {
  form: PropTypes.shape({
    template_type: PropTypes.string.isRequired,
    subject: PropTypes.string,
    heading: PropTypes.string,
    paragraph_text: PropTypes.string,
    button_text: PropTypes.string,
    button_url: PropTypes.string,
    signature: PropTypes.string,
  }).isRequired,
  changeHandler: PropTypes.func.isRequired,
  template: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    category: PropTypes.string.isRequired
  }).isRequired,
  isEdit: PropTypes.bool.isRequired,
}

function TemplateForm({ form, changeHandler, template, isEdit }) {
  const [profileDescription, setProfileDescription] = useState("")
  const [isShowAiModal, setIsShowAiModal] = useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const confirmModal = useConfirmModal()
  const { hasPermission } = usePermissions()
  const normalizedForId = useRef(null)

  const withPhoto = ["small_gallery", "large_gallery"].includes(template.category)
  const isEmail = form.template_type === "email"
  const isText = !isEmail
  const isSmallGallery = template.category === "small_gallery"
  const isParagraphTooShort = (form?.paragraph_text?.length ?? 0) < 25

  const removeTemplateHandler = confirmModal(
    {
      title: "Are you sure?",
      color: "danger",
      submitText: t("global.confirm"),
      cancelText: t("global.cancel")
    },
    () => dispatch(removeTemplate(template.id)).then(() => navigate(oldRoutes.guideMarketingTemplatesPath()))
  )

  const setParagraphText = (value) => changeHandler({ target: { name: "paragraph_text", value } })

  // Convert legacy plain-text content (with line breaks) to HTML once per
  // loaded template so it renders correctly in the rich text editor.
  useEffect(() => {
    if (normalizedForId.current === (template.id ?? "new")) return
    normalizedForId.current = template.id ?? "new"
    const html = toEditorHtml(form.paragraph_text || "")
    if (html !== (form.paragraph_text || "")) setParagraphText(html)
  }, [template.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex vertical gap="large" className={styles.container}>
      <Flex className={styles.typeToggleGroup}>
        <Button
          className={styles.typeToggleButton}
          type={isEmail ? "primary" : "default"}
          onClick={() => changeHandler({ target: { name: "template_type", value: "email" } })}
        >
          {t("marketing.labels.email")}
        </Button>
        <Button
          className={styles.typeToggleButton}
          type={isText ? "primary" : "default"}
          onClick={() => changeHandler({ target: { name: "template_type", value: "text" } })}
        >
          {t("marketing.labels.text")}
        </Button>
      </Flex>

      {isEmail && (
        <div>
          <Text className={styles.fieldLabel}>{t("marketing.labels.subject")}</Text>
          <Input id="subject" type="text" name="subject" value={form.subject || ""} onChange={changeHandler} className={styles.input} withError />
        </div>
      )}

      {template?.category !== "first_basic" && (
        <div>
          <Text className={styles.fieldLabel}>{t("marketing.labels.heading")}</Text>
          <Input id="heading" type="text" name="heading" value={form.heading || ""} onChange={changeHandler} className={styles.input} withError />
        </div>
      )}

      <div>
        <Flex align="center" justify="space-between" wrap className={styles.paragraphHeader}>
          <Text className={styles.fieldLabel}>{t("marketing.labels.paragraph_text")}</Text>
          <Tooltip title={isParagraphTooShort ? t("marketing.ai_enable_text_generation") : ""} placement="top">
            <Button
              type="primary"
              icon={<UIIcon name="aiEnhancement" color={isParagraphTooShort ? colors.textPrimary : colors.disabled} />}
              disabled={isParagraphTooShort}
              onClick={() => setIsShowAiModal(true)}
              className="ml-auto"
            >
              {t("pro.ai_enhancement.button_text")}
            </Button>
          </Tooltip>
        </Flex>
        <RichTextEditor
          id="paragraph_text"
          name="paragraph_text"
          className={styles.input}
          value={form.paragraph_text}
          onChange={setParagraphText}
        />
      </div>

      {withPhoto && isEmail && (
        <div>
          <Text className={styles.fieldLabel}>{t("marketing.labels.image", { max: isSmallGallery ? 2 : 4 })}</Text>
          <PhotoSelector imageHeight="100%" form={form} changeHandler={changeHandler} max={isSmallGallery ? 2 : 4} />
        </div>
      )}

      {template?.category !== "first_basic" && (
        <>
          {isEmail && (
            <div>
              <Text className={styles.fieldLabel}>{t("marketing.labels.button_text")}</Text>
              <Input
                id="button_text"
                type="text"
                name="button_text"
                value={form.button_text || ""}
                onChange={changeHandler}
                className={styles.input}
                withError
              />
            </div>
          )}
          <div>
            <Text className={styles.fieldLabel}>
              {isEmail ? t("marketing.labels.button_link") : t("marketing.labels.text_link")}
            </Text>
            <Input
              id="button_url"
              type="text"
              name="button_url"
              value={form.button_url || ""}
              onChange={changeHandler}
              className={styles.input}
              withError
            />
          </div>
        </>
      )}

      <div>
        <Text className={styles.fieldLabel}>{t("marketing.labels.signature_text")}</Text>
        <Input
          id="signature"
          type="text"
          name="signature"
          value={form.signature || ""}
          onChange={changeHandler}
          className={styles.input}
          withError
        />
      </div>

      {isEdit && hasPermission(MODULES.MARKETING, ACTIONS.EDIT) && (
        <Button className={styles.removeButton} onClick={removeTemplateHandler}>
          {t("marketing.remove_template")}
        </Button>
      )}

      {isShowAiModal && (
        <AiEnhancementModal
          open={isShowAiModal}
          onCancel={() => {
            setIsShowAiModal(false)
            setProfileDescription("")
          }}
          profileDescription={profileDescription}
          description={form.paragraph_text || ""}
          setDescription={setParagraphText}
          setProfileDescription={setProfileDescription}
          onOk={() => setIsShowAiModal(false)}
          text={t("client.marketing_email_content")}
          modalTitle={t("client.marketing_email_enhancer")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("pro.ai_enhancement.form.continue_refinement_details")}
          improveWithAITooltipTitle={t("client.imrove_with_ai_tooltip_for_marketing_email")}
        />
      )}
    </Flex>
  )
}

export default memo(TemplateForm)
